import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Menu, MenuItem, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, Grid, FormControlLabel, Switch } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PricingIcon from '@mui/icons-material/AttachMoney';
import InfoIcon from '@mui/icons-material/Info';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LockIcon from '@mui/icons-material/Lock';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { motion } from 'framer-motion';
//import logo from '../assets/capitol_gains_logo_250x250.png';
import logo from '../assets/capitol_gains_logo_round_500x500.png';
import gold_logo from '../assets/capitol_gains_logo_round_gold.png';

import { checkUserGroup } from '../utils/authUtils'; // Import the helper function

const navButtonSx = (active) => ({
    color: active ? 'text.primary' : 'text.secondary',
    fontWeight: 500,
    px: 1.5,
    fontSize: '0.875rem',
    letterSpacing: 0,
    textTransform: 'none',
    '&:hover': { color: 'text.primary', bgcolor: 'rgba(255,255,255,0.04)' },
});

function Navbar({ user, signOut, toggleDollars, dollarsEnabled }) {
    const [settingsEl, setSettingsEl] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [isPremiumUser, setIsPremiumUser] = useState(false);
    const [isAdminUser, setIsAdminUser] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const location = useLocation(); // Use this hook to get the current route

    useEffect(() => {
        const fetchUserGroup = async () => {
            if (user) {
                try {
                    const { isPro, isAdmin } = await checkUserGroup();
                    setIsPremiumUser(isPro);
                    setIsAdminUser(isAdmin);
                } catch (err) {
                    console.error('Error fetching user group:', err);
                }
            }
        };
        fetchUserGroup();
    }, [user]);

    const handleSettingsOpen = (event) => {
        setSettingsEl(event.currentTarget);
    };

    const handleSettingsClose = () => {
        setSettingsEl(null);
    };

    const handleSignOutClick = () => {
        handleSettingsClose();
        signOut();
    };

    const toggleDrawer = (open) => () => {
        setDrawerOpen(open);
    };

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                backgroundColor: 'custom.surface.raised',
                borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                backdropFilter: 'blur(8px)',
                top: 0,
                zIndex: theme.zIndex.drawer + 1,
            }}
        >
            <Toolbar>
                <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item xs={8} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Link
                            to="/"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                                color: 'inherit',
                            }}
                            onMouseEnter={() => setHovered(true)}
                            onMouseLeave={() => setHovered(false)}
                            onClick={() => setHovered(false)}
                        >
                            <motion.div
                                animate={{
                                    scale: hovered ? 1.02 : 1,
                                    filter: hovered ? 'drop-shadow(0 6px 10px rgba(0, 0, 0, 0.4))' : 'none',
                                }}
                                transition={{ type: 'spring', stiffness: 2000, damping: 30 }}
                                style={{ display: 'flex', alignItems: 'center' }}
                            >
                                <img
                                    src={isPremiumUser ? gold_logo : logo}
                                    alt="CapitolGains Logo"
                                    style={{ height: 32, marginRight: 10 }}
                                />
                                <Typography
                                    component="span"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.9375rem',
                                        letterSpacing: '-0.01em',
                                        whiteSpace: 'nowrap',
                                        color: 'text.primary',
                                    }}
                                >
                                    Capitol Gains
                                </Typography>
                                {isPremiumUser && (
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: 1,
                                            px: 0.75,
                                            py: '2px',
                                            borderRadius: 0.75,
                                            fontSize: '0.625rem',
                                            fontWeight: 600,
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            color: '#fbbf24',
                                            bgcolor: 'rgba(251,191,36,0.1)',
                                            border: '1px solid rgba(251,191,36,0.25)',
                                        }}
                                    >
                                        Pro
                                    </Box>
                                )}
                            </motion.div>
                        </Link>
                    </Grid>

                    {!isMobile && (
                        <Grid item md={8} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Button component={Link} to="/recent-trades" sx={navButtonSx(location.pathname === '/recent-trades')}>
                                    Recent Trades
                                </Button>
                                <Button
                                    component={Link}
                                    to="/stocks"
                                    sx={navButtonSx(location.pathname === '/stocks')}
                                    startIcon={!isPremiumUser ? <LockIcon sx={{ fontSize: 12 }} /> : null}
                                >
                                    Stock Rankings
                                </Button>
                                <Button component={Link} to="/issuers" sx={navButtonSx(location.pathname.startsWith('/issuers'))}>
                                    Issuers
                                </Button>
                                <Button
                                    component={Link}
                                    to="/options"
                                    sx={navButtonSx(location.pathname === '/options')}
                                    startIcon={!isPremiumUser ? <LockIcon sx={{ fontSize: 12 }} /> : null}
                                >
                                    Options
                                </Button>
                                <Button component={Link} to="/leaderboard" sx={navButtonSx(location.pathname === '/leaderboard')}>
                                    Leaderboard
                                </Button>
                                <Button component={Link} to="/track-record" sx={navButtonSx(location.pathname === '/track-record')}>
                                    Track Record
                                </Button>
                                <Button component={Link} to="/analytics" sx={navButtonSx(location.pathname === '/analytics')}>
                                    Analytics
                                </Button>
                                <Button component={Link} to="/pricing" sx={navButtonSx(location.pathname === '/pricing')}>
                                    Pricing
                                </Button>
                                <Button component={Link} to="/about" sx={navButtonSx(location.pathname === '/about')}>
                                    About
                                </Button>
                            </Box>
                        </Grid>
                    )}

                    <Grid item xs={4} md={2} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {isMobile ? (
                            <>
                                <IconButton
                                    edge="end"
                                    color="inherit"
                                    aria-label="menu"
                                    onClick={toggleDrawer(true)}
                                    sx={{ mr: 1 }}
                                >
                                    <MenuIcon />
                                </IconButton>
                                <Drawer
                                    anchor="right"
                                    open={drawerOpen}
                                    onClose={toggleDrawer(false)}
                                >
                                    <Box
                                        sx={{ width: 250, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
                                    >
                                        <List>
                                            <ListItem>
                                                <IconButton edge="start" color="inherit" onClick={toggleDrawer(false)}>
                                                    <CloseIcon />
                                                </IconButton>
                                            </ListItem>
                                            <Divider />
                                            <ListItem button component={Link} to="/stocks" onClick={toggleDrawer(false)}>
                                                <ListItemIcon>{isPremiumUser ? <BarChartIcon /> : <LockIcon />}</ListItemIcon>
                                                <ListItemText primary="Stock Rankings" />
                                            </ListItem>
                                            <ListItem button component={Link} to="/leaderboard" onClick={toggleDrawer(false)}>
                                                <ListItemIcon><LeaderboardIcon /></ListItemIcon>
                                                <ListItemText primary="Congress Leaderboard" />
                                            </ListItem>
                                            <ListItem button component={Link} to="/issuers" onClick={toggleDrawer(false)}>
                                                <ListItemIcon><BarChartIcon /></ListItemIcon>
                                                <ListItemText primary="Issuers" />
                                            </ListItem>
                                            <ListItem button component={Link} to="/options" onClick={toggleDrawer(false)}>
                                                <ListItemIcon>{isPremiumUser ? <TrendingUpIcon /> : <LockIcon />}</ListItemIcon>
                                                <ListItemText primary="Options" />
                                            </ListItem>
                                            <ListItem button component={Link} to="/recent-trades" onClick={toggleDrawer(false)}>
                                                <ListItemIcon><TrendingUpIcon /></ListItemIcon>
                                                <ListItemText primary="Recent Trades" />
                                            </ListItem>
                                            <ListItem button component={Link} to="/track-record" onClick={toggleDrawer(false)}>
                                                <ListItemIcon><TrendingUpIcon /></ListItemIcon>
                                                <ListItemText primary="Track Record" />
                                            </ListItem>
                                            <ListItem button component={Link} to="/analytics" onClick={toggleDrawer(false)}>
                                                <ListItemIcon><BarChartIcon /></ListItemIcon>
                                                <ListItemText primary="Analytics" />
                                            </ListItem>
                                            <ListItem button component={Link} to="/pricing" onClick={toggleDrawer(false)}>
                                                <ListItemIcon><PricingIcon /></ListItemIcon>
                                                <ListItemText primary="Pricing" />
                                            </ListItem>
                                            <ListItem button component={Link} to="/about" onClick={toggleDrawer(false)}>
                                                <ListItemIcon><InfoIcon /></ListItemIcon>
                                                <ListItemText primary="About Us" />
                                            </ListItem>
                                            {user && (
                                                <ListItem button component={Link} to="/account" onClick={toggleDrawer(false)}>
                                                    <ListItemIcon><AccountCircleIcon /></ListItemIcon>
                                                    <ListItemText primary="Account" />
                                                </ListItem>
                                            )}
                                        </List>
                                        <Divider />
                                        {user && (
                                            <ListItem button onClick={handleSignOutClick} sx={{ mt: 'auto' }}>
                                                <ListItemIcon><ExitToAppIcon /></ListItemIcon>
                                                <ListItemText primary="Sign Out" />
                                            </ListItem>
                                        )}
                                    </Box>
                                </Drawer>
                            </>
                        ) : (
                            <>
                                {/* Conditionally render the "Make it Rain" toggle only on the home page */}
                                {location.pathname === '/' && (
                                    <FormControlLabel
                                        control={<Switch checked={dollarsEnabled} onChange={toggleDollars} size="small" />}
                                        label="Make it Rain"
                                        labelPlacement="start"
                                        sx={{ color: 'text.secondary', mr: 1, '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' } }}
                                    />
                                )}
                                {user ? (
                                    <>
                                        <IconButton
                                            edge="end"
                                            color="inherit"
                                            aria-label="settings"
                                            onClick={handleSettingsOpen}
                                        >
                                            <SettingsIcon />
                                        </IconButton>
                                        <Menu
                                            anchorEl={settingsEl}
                                            open={Boolean(settingsEl)}
                                            onClose={handleSettingsClose}
                                        >
                                            <MenuItem onClick={handleSettingsClose} component={Link} to="/account">
                                                Account
                                            </MenuItem>
                                            {isAdminUser && (
                                                <MenuItem onClick={handleSettingsClose} component={Link} to="/admin">
                                                    Admin
                                                </MenuItem>
                                            )}
                                            <MenuItem onClick={handleSignOutClick}>
                                                Sign Out
                                            </MenuItem>
                                        </Menu>
                                    </>
                                ) : (
                                    <Button component={Link} to="/sign-in" variant="contained" size="small">
                                        Sign in
                                    </Button>
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            </Toolbar>
        </AppBar>
    );
}

export default Navbar;
